<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Start Household Visit</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Household Info -->
          <div class="card bg-light mb-4">
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <h6 class="mb-2">{{ household.household_number }}</h6>
                  <p class="mb-1">
                    <strong>Head:</strong> {{ household.household_head_name || 'Not assigned' }}
                  </p>
                  <p class="mb-1">
                    <strong>Phone:</strong> {{ household.head_phone || 'No phone' }}
                  </p>
                  <p class="mb-0">
                    <strong>Location:</strong> {{ household.kebele_name }}, {{ household.woreda_name }}
                  </p>
                </div>
                <div class="col-md-6">
                  <p class="mb-1">
                    <strong>Members:</strong>
                    <span class="badge bg-primary">{{ household.member_count || 0 }} total</span>
                    <span v-if="household.cbhi_member_count > 0" class="badge bg-success ms-1">
                      {{ household.cbhi_member_count }} CBHI
                    </span>
                  </p>
                  <p class="mb-1">
                    <strong>Special Groups:</strong>
                    <span v-if="household.under_five_count > 0" class="badge bg-warning me-1">
                      <i class="bx bx-child"></i> {{ household.under_five_count }} Under 5
                    </span>
                    <span v-if="household.pregnant_women_count > 0" class="badge bg-danger">
                      <i class="bx bx-female"></i> {{ household.pregnant_women_count }} Pregnant
                    </span>
                  </p>
                  <p class="mb-0">
                    <strong>Last Visit:</strong>
                    <span v-if="household.last_visit_date">
                      {{ formatDate(household.last_visit_date) }}
                      ({{ getDaysSince(household.last_visit_date) }} days ago)
                    </span>
                    <span v-else class="text-muted">Never visited</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Visit Form -->
          <form @submit.prevent="startVisit">
            <!-- Visit Type -->
            <div class="mb-3">
              <label class="form-label">Visit Type <span class="text-danger">*</span></label>
              <select v-model="visitForm.visitType" class="form-select" required>
                <option value="">Select visit type</option>
                <option value="routine">Routine Health Promotion</option>
                <option value="follow_up">Follow-up Visit</option>
                <option value="emergency">Emergency/Urgent</option>
                <option value="campaign">Campaign Activity</option>
                <option value="assessment">Environmental Assessment</option>
                <option value="anc">Antenatal Care</option>
                <option value="pnc">Postnatal Care</option>
                <option value="immunization">Immunization</option>
                <option value="nutrition">Nutrition Screening</option>
              </select>
            </div>

            <!-- Visit Purpose -->
            <div class="mb-3">
              <label class="form-label">Visit Purpose <span class="text-danger">*</span></label>
              <textarea v-model="visitForm.visitPurpose" class="form-control" rows="3"
                        placeholder="Describe the purpose of this visit..." required></textarea>
            </div>

            <!-- Quick Checklist -->
            <div class="mb-3">
              <label class="form-label">Quick Assessment Checklist</label>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-check mb-2">
                    <input v-model="visitForm.checklist.checkPregnantWomen"
                           class="form-check-input" type="checkbox"
                           id="checkPregnantWomen">
                    <label class="form-check-label" for="checkPregnantWomen">
                      Check for pregnant women
                    </label>
                  </div>
                  <div class="form-check mb-2">
                    <input v-model="visitForm.checklist.checkVaccinations"
                           class="form-check-input" type="checkbox"
                           id="checkVaccinations">
                    <label class="form-check-label" for="checkVaccinations">
                      Review vaccination status
                    </label>
                  </div>
                  <div class="form-check mb-2">
                    <input v-model="visitForm.checklist.checkNutrition"
                           class="form-check-input" type="checkbox"
                           id="checkNutrition">
                    <label class="form-check-label" for="checkNutrition">
                      Assess child nutrition
                    </label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check mb-2">
                    <input v-model="visitForm.checklist.checkEnvironment"
                           class="form-check-input" type="checkbox"
                           id="checkEnvironment">
                    <label class="form-check-label" for="checkEnvironment">
                      Environmental assessment
                    </label>
                  </div>
                  <div class="form-check mb-2">
                    <input v-model="visitForm.checklist.healthEducation"
                           class="form-check-input" type="checkbox"
                           id="healthEducation">
                    <label class="form-check-label" for="healthEducation">
                      Provide health education
                    </label>
                  </div>
                  <div class="form-check mb-2">
                    <input v-model="visitForm.checklist.checkCBHI"
                           class="form-check-input" type="checkbox"
                           id="checkCBHI">
                    <label class="form-check-label" for="checkCBHI">
                      CBHI enrollment status
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Priority Alerts -->
            <div v-if="getPriorityAlerts().length > 0" class="mb-3">
              <label class="form-label text-danger">
                <i class="bx bx-error-circle"></i> Priority Attention Needed
              </label>
              <div class="alert alert-danger">
                <ul class="mb-0">
                  <li v-for="(alert, index) in getPriorityAlerts()" :key="index">
                    {{ alert }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- GPS Location -->
            <div class="mb-3">
              <div class="form-check">
                <input v-model="captureGPS" class="form-check-input" type="checkbox" id="captureGPSCheck">
                <label class="form-check-label" for="captureGPSCheck">
                  Capture GPS Location at household
                </label>
              </div>
              <div v-if="gpsStatus" class="mt-2">
                <span v-if="gpsLoading" class="text-primary">
                  <i class="bx bx-loader-alt bx-spin"></i> Getting location...
                </span>
                <span v-else-if="gpsError" class="text-danger">
                  <i class="bx bx-error-circle"></i> {{ gpsError }}
                </span>
                <span v-else class="text-success">
                  <i class="bx bx-check-circle"></i> Location captured
                  <small class="text-muted ms-2">
                    ({{ gpsCoordinates.latitude.toFixed(6) }}, {{ gpsCoordinates.longitude.toFixed(6) }})
                  </small>
                </span>
              </div>
            </div>

            <!-- Materials/Supplies Check -->
            <div class="mb-3">
              <label class="form-label">Materials/Supplies Needed</label>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-check">
                    <input v-model="visitForm.supplies.vaccines"
                           class="form-check-input" type="checkbox" id="suppliesVaccines">
                    <label class="form-check-label" for="suppliesVaccines">
                      Vaccines & Cold Chain
                    </label>
                  </div>
                  <div class="form-check">
                    <input v-model="visitForm.supplies.vitaminA"
                           class="form-check-input" type="checkbox" id="suppliesVitaminA">
                    <label class="form-check-label" for="suppliesVitaminA">
                      Vitamin A Supplements
                    </label>
                  </div>
                  <div class="form-check">
                    <input v-model="visitForm.supplies.dewormingMeds"
                           class="form-check-input" type="checkbox" id="suppliesDeworming">
                    <label class="form-check-label" for="suppliesDeworming">
                      Deworming Medication
                    </label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check">
                    <input v-model="visitForm.supplies.familyPlanning"
                           class="form-check-input" type="checkbox" id="suppliesFP">
                    <label class="form-check-label" for="suppliesFP">
                      Family Planning Supplies
                    </label>
                  </div>
                  <div class="form-check">
                    <input v-model="visitForm.supplies.educationMaterials"
                           class="form-check-input" type="checkbox" id="suppliesEducation">
                    <label class="form-check-label" for="suppliesEducation">
                      Education Materials
                    </label>
                  </div>
                  <div class="form-check">
                    <input v-model="visitForm.supplies.muacTape"
                           class="form-check-input" type="checkbox" id="suppliesMUAC">
                    <label class="form-check-label" for="suppliesMUAC">
                      MUAC Tape
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Estimated Duration -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Estimated Duration</label>
                <select v-model="visitForm.estimatedDuration" class="form-select">
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Travel Time (minutes)</label>
                <input v-model.number="visitForm.travelTime" type="number"
                       class="form-control" min="0" placeholder="0">
              </div>
            </div>
          </form>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="button" class="btn btn-success" @click="startVisit"
                  :disabled="!isFormValid || submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Starting...
            </span>
            <span v-else>
              <i class="bx bx-play-circle"></i> Start Visit
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "StartVisitModal",
  props: {
    household: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,

      // Visit form
      visitForm: {
        visitType: '',
        visitPurpose: '',
        estimatedDuration: '30',
        travelTime: 0,
        checklist: {
          checkPregnantWomen: false,
          checkVaccinations: false,
          checkNutrition: false,
          checkEnvironment: false,
          healthEducation: false,
          checkCBHI: false
        },
        supplies: {
          vaccines: false,
          vitaminA: false,
          dewormingMeds: false,
          familyPlanning: false,
          educationMaterials: false,
          muacTape: false
        }
      },

      // GPS
      captureGPS: false,
      gpsLoading: false,
      gpsError: null,
      gpsStatus: false,
      gpsCoordinates: {
        latitude: null,
        longitude: null
      }
    };
  },

  computed: {
    isFormValid() {
      return this.visitForm.visitType && this.visitForm.visitPurpose;
    }
  },

  watch: {
    captureGPS(value) {
      if (value) {
        this.getGPSLocation();
      } else {
        this.gpsCoordinates = { latitude: null, longitude: null };
        this.gpsStatus = false;
      }
    },

    'household.under_five_count': {
      handler(value) {
        if (value > 0) {
          this.visitForm.checklist.checkVaccinations = true;
          this.visitForm.checklist.checkNutrition = true;
        }
      },
      immediate: true
    },

    'household.pregnant_women_count': {
      handler(value) {
        if (value > 0) {
          this.visitForm.checklist.checkPregnantWomen = true;
        }
      },
      immediate: true
    }
  },

  methods: {
    getPriorityAlerts() {
      const alerts = [];

      if (this.household.overdue_vaccinations > 0) {
        alerts.push(`${this.household.overdue_vaccinations} overdue vaccinations`);
      }

      if (this.household.pregnant_women_count > 0 && !this.household.recent_anc_visit) {
        alerts.push('Pregnant women need ANC follow-up');
      }

      if (!this.household.last_visit_date || this.getDaysSince(this.household.last_visit_date) > 90) {
        alerts.push('Household not visited in over 90 days');
      }

      if (this.household.is_vulnerable) {
        alerts.push('Vulnerable household - needs special attention');
      }

      return alerts;
    },

    async getGPSLocation() {
      if (!navigator.geolocation) {
        this.gpsError = "Geolocation is not supported by your browser";
        return;
      }

      this.gpsLoading = true;
      this.gpsError = null;
      this.gpsStatus = true;

      navigator.geolocation.getCurrentPosition(
          (position) => {
            this.gpsCoordinates = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            };
            this.gpsLoading = false;
          },
          (error) => {
            this.gpsLoading = false;
            switch(error.code) {
              case error.PERMISSION_DENIED:
                this.gpsError = "Location permission denied";
                break;
              case error.POSITION_UNAVAILABLE:
                this.gpsError = "Location information unavailable";
                break;
              case error.TIMEOUT:
                this.gpsError = "Location request timed out";
                break;
              default:
                this.gpsError = "An unknown error occurred";
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
      );
    },

    async startVisit() {
      if (!this.isFormValid) return;

      this.submitting = true;

      // Build purpose with checklist items
      let fullPurpose = this.visitForm.visitPurpose;
      const checklistItems = [];

      if (this.visitForm.checklist.checkPregnantWomen) checklistItems.push('ANC check');
      if (this.visitForm.checklist.checkVaccinations) checklistItems.push('Vaccination review');
      if (this.visitForm.checklist.checkNutrition) checklistItems.push('Nutrition assessment');
      if (this.visitForm.checklist.checkEnvironment) checklistItems.push('Environmental assessment');
      if (this.visitForm.checklist.healthEducation) checklistItems.push('Health education');
      if (this.visitForm.checklist.checkCBHI) checklistItems.push('CBHI enrollment');

      if (checklistItems.length > 0) {
        fullPurpose += ` | Planned: ${checklistItems.join(', ')}`;
      }

      const data = {
        "function": "createHouseholdVisit",
        "householdId": this.household.household_id,
        "visitType": this.visitForm.visitType,
        "visitPurpose": fullPurpose,
        "gpsLatitude": this.gpsCoordinates.latitude,
        "gpsLongitude": this.gpsCoordinates.longitude
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('started', {
            visitId: res.data.data.visitId,
            householdId: res.data.data.householdId,
            checklist: this.visitForm.checklist,
            supplies: this.visitForm.supplies
          });
        } else {
          alert(res.data.message || 'Failed to start visit');
        }
      } catch (e) {
        console.error("Failed to start visit:", e);
        alert('Failed to start visit. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    getDaysSince(dateString) {
      if (!dateString) return 0;
      const date = new Date(dateString);
      const today = new Date();
      const diffTime = Math.abs(today - date);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.bx-spin {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}
</style>